import { useEffect, useMemo, useRef, useState } from "react";
import { hasRole } from "@/lib/roles";
import api, { formatApiError } from "@/lib/api";
import EmployeePicker from "@/components/EmployeePicker";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmProvider";
import { Plus, X, Trash2, Search, Repeat, Power, PowerOff, Edit3, Upload, Download, FileText } from "lucide-react";
import { MoneyInput } from "@/components/MoneyInput";

function fmt(n) {
    if (n == null) return "₦0";
    return "₦" + Number(n).toLocaleString("en-NG", { maximumFractionDigits: 0 });
}

function currentPeriod() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const emptyForm = () => ({
    employee_id: "",
    label: "",
    amount: "",
    start_period: currentPeriod(),
    end_period: "",
    active: true,
    note: "",
});

export default function RecurringDeductions() {
    const { user } = useAuth();
    const { confirm } = useConfirm();
    const role = user?.role || "";
    const canManage = hasRole(user, "admin", "hr");

    const [items, setItems] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [activeFilter, setActiveFilter] = useState("all"); // all | active | inactive
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null); // null = create
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const [{ data: rds }, { data: emps }] = await Promise.all([
                api.get("/recurring-deductions"),
                role === "employee" ? Promise.resolve({ data: [] }) : api.get("/employees"),
            ]);
            setItems(Array.isArray(rds) ? rds : []);
            setEmployees(Array.isArray(emps) ? emps : []);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not load recurring deductions");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return items.filter((r) => {
            if (activeFilter === "active" && !r.active) return false;
            if (activeFilter === "inactive" && r.active) return false;
            if (!q) return true;
            return (
                (r.employee_name || "").toLowerCase().includes(q) ||
                (r.employee_no || "").toLowerCase().includes(q) ||
                (r.label || "").toLowerCase().includes(q) ||
                (r.department || "").toLowerCase().includes(q) ||
                (r.division || "").toLowerCase().includes(q)
            );
        });
    }, [items, search, activeFilter]);

    const totals = useMemo(() => {
        const active = items.filter((r) => r.active);
        const monthly = active.reduce((s, r) => s + (Number(r.amount) || 0), 0);
        return { count: items.length, active: active.length, monthly };
    }, [items]);

    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm());
        setDialogOpen(true);
    };

    const openEdit = (rd) => {
        setEditing(rd);
        setForm({
            employee_id: rd.employee_id,
            label: rd.label || "",
            amount: String(rd.amount ?? ""),
            start_period: rd.start_period || currentPeriod(),
            end_period: rd.end_period || "",
            active: !!rd.active,
            note: rd.note || "",
        });
        setDialogOpen(true);
    };

    const onSave = async (e) => {
        e?.preventDefault?.();
        if (!form.employee_id) {
            toast.error("Pick an employee");
            return;
        }
        if (!form.label.trim()) {
            toast.error("Label is required (e.g. Cooperative dues)");
            return;
        }
        const amt = Number(form.amount);
        if (form.amount === "" || Number.isNaN(amt) || amt < 0) {
            toast.error("Enter an amount of ₦0 or more");
            return;
        }
        const payload = {
            employee_id: form.employee_id,
            label: form.label.trim(),
            amount: amt,
            start_period: form.start_period || currentPeriod(),
            end_period: form.end_period || null,
            active: !!form.active,
            note: (form.note || "").trim(),
        };
        setSaving(true);
        try {
            if (editing) {
                // Only send fields the update endpoint accepts
                const upd = {
                    label: payload.label,
                    amount: payload.amount,
                    start_period: payload.start_period,
                    end_period: payload.end_period,
                    active: payload.active,
                    note: payload.note,
                };
                await api.put(`/recurring-deductions/${editing.id}`, upd);
                toast.success("Recurring deduction updated");
            } else {
                await api.post("/recurring-deductions", payload);
                toast.success("Recurring deduction added — applies from " + payload.start_period);
            }
            setDialogOpen(false);
            await refresh();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const onToggleActive = async (rd) => {
        try {
            await api.put(`/recurring-deductions/${rd.id}`, { active: !rd.active });
            toast.success(rd.active ? "Paused" : "Resumed");
            await refresh();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not update");
        }
    };

    const onDelete = async (rd) => {
        const ok = await confirm({
            title: "Delete recurring deduction?",
            description: `This will permanently remove "${rd.label}" for ${rd.employee_name}. Existing payroll runs already generated are not affected.`,
            confirmText: "Delete",
            destructive: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/recurring-deductions/${rd.id}`);
            toast.success("Deleted");
            await refresh();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not delete");
        }
    };

    return (
        <div className="px-8 md:px-12 py-12 md:py-16" data-testid="recurring-deductions-page">
            {/* Header */}
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Payroll automation</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02]">
                        Recurring deductions
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-2xl">
                        Set up a deduction once and it applies automatically to every monthly payroll run between
                        the start and end period. Use this for cooperative dues, union dues, rent recoveries,
                        garnishments or any other standing-order style deduction.
                    </p>
                </div>
                {canManage && (
                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            type="button"
                            onClick={() => setBulkOpen(true)}
                            className="inline-flex items-center gap-2 border border-klein text-klein px-5 py-3 text-sm hover:bg-klein hover:text-white"
                            data-testid="recurring-bulk-btn"
                            title="Bulk-upload recurring deductions via Excel"
                        >
                            <Upload className="w-4 h-4" /> Bulk upload
                        </button>
                        <button
                            type="button"
                            onClick={openCreate}
                            className="inline-flex items-center gap-2 bg-klein text-white px-5 py-3 text-sm hover:bg-klein/90"
                            data-testid="recurring-add-btn"
                        >
                            <Plus className="w-4 h-4" /> Add deduction
                        </button>
                    </div>
                )}
            </div>

            {/* Stats */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <StatCard label="Total rules" value={totals.count} testid="recurring-stat-total" />
                <StatCard label="Currently active" value={totals.active} testid="recurring-stat-active" />
                <StatCard label="Monthly impact" value={fmt(totals.monthly)} testid="recurring-stat-monthly" />
            </div>

            {/* Filters */}
            <div className="mt-8 flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[220px] max-w-md">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search name, staff no, department, division, label…"
                        className="w-full pl-10 pr-3 py-2.5 border border-ink/15 text-sm focus:border-klein focus:outline-none"
                        data-testid="recurring-search"
                    />
                </div>
                <div className="inline-flex border border-ink/15" role="group">
                    {[
                        { id: "all", label: "All" },
                        { id: "active", label: "Active" },
                        { id: "inactive", label: "Paused" },
                    ].map((f) => (
                        <button
                            key={f.id}
                            type="button"
                            onClick={() => setActiveFilter(f.id)}
                            className={`px-4 py-2 text-xs uppercase tracking-[0.15em] ${
                                activeFilter === f.id ? "bg-ink text-white" : "text-ink/70 hover:bg-ink/5"
                            }`}
                            data-testid={`recurring-filter-${f.id}`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Table */}
            <div className="mt-6 bg-white border border-ink/10" data-testid="recurring-table">
                {loading ? (
                    <div className="px-6 py-16 text-center text-ink/50 text-sm">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="px-6 py-16 text-center">
                        <Repeat className="w-8 h-8 mx-auto text-ink/30" strokeWidth={1.5} />
                        <p className="mt-3 text-sm text-ink/60">
                            {items.length === 0 ? "No recurring deductions yet." : "No matches for your filter."}
                        </p>
                        {canManage && items.length === 0 && (
                            <button
                                type="button"
                                onClick={openCreate}
                                className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.15em] border border-klein text-klein hover:bg-klein hover:text-white"
                            >
                                <Plus className="w-3.5 h-3.5" /> Add your first one
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-ink/10 text-ink/50 text-[11px] uppercase tracking-[0.15em]">
                                    <th className="text-left px-4 py-3 font-medium">Employee</th>
                                    <th className="text-left px-4 py-3 font-medium">Label</th>
                                    <th className="text-right px-4 py-3 font-medium">Amount / month</th>
                                    <th className="text-left px-4 py-3 font-medium">Window</th>
                                    <th className="text-left px-4 py-3 font-medium">Status</th>
                                    <th className="text-right px-4 py-3 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((rd) => (
                                    <tr key={rd.id} className="border-b border-ink/5 hover:bg-ink/[0.015]" data-testid={`recurring-row-${rd.id}`}>
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-ink">{rd.employee_name || "—"}</div>
                                            <div className="text-[11px] text-ink/50">
                                                {rd.employee_no || ""}{rd.department ? ` · ${rd.department}` : ""}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="text-ink">{rd.label}</div>
                                            {rd.note && <div className="text-[11px] text-ink/50">{rd.note}</div>}
                                        </td>
                                        <td className="px-4 py-3 text-right font-mono">{fmt(rd.amount)}</td>
                                        <td className="px-4 py-3 text-ink/70 whitespace-nowrap">
                                            {rd.start_period || "—"}
                                            <span className="text-ink/40 mx-1">→</span>
                                            {rd.end_period || <span className="text-ink/40">open</span>}
                                        </td>
                                        <td className="px-4 py-3">
                                            {rd.active ? (
                                                <span className="inline-flex items-center px-2 py-1 border border-green-200 bg-green-50 text-green-700 text-[11px] uppercase tracking-[0.15em]">
                                                    Active
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2 py-1 border border-ink/10 bg-ink/5 text-ink/60 text-[11px] uppercase tracking-[0.15em]">
                                                    Paused
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-end gap-1">
                                                {canManage && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            onClick={() => onToggleActive(rd)}
                                                            title={rd.active ? "Pause" : "Resume"}
                                                            className="p-2 text-ink/60 hover:text-ink hover:bg-ink/5"
                                                            data-testid={`recurring-toggle-${rd.id}`}
                                                        >
                                                            {rd.active
                                                                ? <PowerOff className="w-4 h-4" strokeWidth={1.5} />
                                                                : <Power className="w-4 h-4" strokeWidth={1.5} />}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => openEdit(rd)}
                                                            title="Edit"
                                                            className="p-2 text-ink/60 hover:text-ink hover:bg-ink/5"
                                                            data-testid={`recurring-edit-${rd.id}`}
                                                        >
                                                            <Edit3 className="w-4 h-4" strokeWidth={1.5} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => onDelete(rd)}
                                                            title="Delete"
                                                            className="p-2 text-ink/60 hover:text-destructive hover:bg-destructive/5"
                                                            data-testid={`recurring-delete-${rd.id}`}
                                                        >
                                                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Dialog */}
            {dialogOpen && (
                <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-4" data-testid="recurring-dialog">
                    <form onSubmit={onSave} className="bg-white max-w-lg w-full p-7">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <div className="overline">{editing ? "Edit" : "New"}</div>
                                <h2 className="mt-1 font-display font-bold text-2xl text-ink">
                                    {editing ? "Edit recurring deduction" : "Add recurring deduction"}
                                </h2>
                            </div>
                            <button type="button" onClick={() => setDialogOpen(false)} className="p-2 text-ink/50 hover:text-ink" data-testid="recurring-dialog-close">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="mt-6 space-y-4">
                            <div>
                                <label className="overline block mb-2">Employee *</label>
                                <EmployeePicker employees={employees} value={form.employee_id} onChange={(v) => setForm({ ...form, employee_id: v })} disabled={!!editing} placeholder="— Pick an employee —" className="py-2.5" testid="recurring-form-employee" />
                            </div>

                            <div>
                                <label className="overline block mb-2">Label *</label>
                                <input
                                    value={form.label}
                                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                                    placeholder="e.g. Cooperative dues, Union dues, Rent recovery"
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                    data-testid="recurring-form-label"
                                    required
                                />
                            </div>

                            <div>
                                <label className="overline block mb-2">Monthly amount (₦) *</label>
                                <MoneyInput
                                    value={form.amount}
                                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                    placeholder="e.g. 5,000"
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                    data-testid="recurring-form-amount"
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="overline block mb-2">Start period *</label>
                                    <input
                                        type="month"
                                        value={form.start_period}
                                        onChange={(e) => setForm({ ...form, start_period: e.target.value })}
                                        className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                        data-testid="recurring-form-start"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="overline block mb-2">End period <span className="text-ink/40">(optional)</span></label>
                                    <input
                                        type="month"
                                        value={form.end_period}
                                        onChange={(e) => setForm({ ...form, end_period: e.target.value })}
                                        placeholder="Open-ended"
                                        className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                        data-testid="recurring-form-end"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="overline block mb-2">Note <span className="text-ink/40">(optional)</span></label>
                                <textarea
                                    rows="2"
                                    value={form.note}
                                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                                    placeholder="Why this deduction is in place"
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                    data-testid="recurring-form-note"
                                />
                            </div>

                            <label className="inline-flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.active}
                                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                                    className="w-4 h-4 accent-klein"
                                    data-testid="recurring-form-active"
                                />
                                <span className="text-sm text-ink">Active — apply to upcoming payroll runs</span>
                            </label>
                        </div>

                        <div className="mt-7 flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setDialogOpen(false)}
                                className="px-4 py-2 text-sm text-ink/70 hover:text-ink"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="inline-flex items-center gap-2 bg-klein text-white px-5 py-2.5 text-sm hover:bg-klein/90 disabled:opacity-60"
                                data-testid="recurring-form-save"
                            >
                                {saving ? "Saving…" : editing ? "Save changes" : "Add deduction"}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {bulkOpen && (
                <BulkRecurringDialog
                    onClose={() => setBulkOpen(false)}
                    onDone={async () => {
                        setBulkOpen(false);
                        await refresh();
                    }}
                />
            )}
        </div>
    );
}

function StatCard({ label, value, testid }) {
    return (
        <div className="bg-white border border-ink/10 p-5" data-testid={testid}>
            <div className="overline">{label}</div>
            <div className="mt-2 font-display font-bold text-2xl text-ink">{value}</div>
        </div>
    );
}

function BulkRecurringDialog({ onClose, onDone }) {
    const [downloading, setDownloading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const fileRef = useRef(null);

    const onDownload = async () => {
        setDownloading(true);
        try {
            const { data } = await api.get("/bulk/recurring-deduction-template", { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = "recurring_deduction_template.xlsx";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Template downloaded. Fill label + amount + start_period, then upload.");
        } catch {
            toast.error("Could not download template");
        } finally {
            setDownloading(false);
        }
    };

    const onPick = () => fileRef.current?.click();
    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".xlsx")) {
            toast.error("Please upload an .xlsx file");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/bulk/recurring-deduction/import", fd);
            setResult(data);
            toast.success(`Created ${data.created_count} recurring deduction${data.created_count === 1 ? "" : "s"} · ₦${Number(data.total_monthly).toLocaleString()} / month`);
            onDone?.(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="recurring-bulk-dialog">
            <div className="bg-white max-w-xl w-full rounded-lg shadow-2xl">
                <div className="px-6 py-4 border-b border-ink/10 flex items-center justify-between">
                    <h2 className="font-display font-bold text-lg flex items-center gap-2">
                        <FileText className="w-4 h-4 text-klein" /> Bulk recurring deductions via Excel
                    </h2>
                    <button onClick={onClose} className="text-ink/40 hover:text-ink" data-testid="recurring-bulk-close">✕</button>
                </div>
                <div className="px-6 py-5 space-y-5">
                    <ol className="text-sm text-ink/70 space-y-2 list-decimal list-inside">
                        <li>Download the template — pre-filled with active employees.</li>
                        <li>For each employee you want to set up: fill <code className="bg-bone px-1 rounded">label</code>, <code className="bg-bone px-1 rounded">amount</code> (NGN/month) and <code className="bg-bone px-1 rounded">start_period</code> (YYYY-MM).</li>
                        <li>Optionally fill <code className="bg-bone px-1 rounded">end_period</code> (YYYY-MM) — leave blank for open-ended.</li>
                        <li>Leave amount blank to skip a row. Upload — each row becomes an active recurring rule.</li>
                    </ol>

                    <div className="grid grid-cols-2 gap-3">
                        <button
                            onClick={onDownload}
                            disabled={downloading}
                            className="inline-flex items-center justify-center gap-2 border border-klein text-klein px-4 py-3 text-sm rounded hover:bg-klein hover:text-white disabled:opacity-50"
                            data-testid="recurring-bulk-download"
                        >
                            <Download className="w-4 h-4" />
                            {downloading ? "Downloading…" : "1. Download template"}
                        </button>
                        <button
                            onClick={onPick}
                            disabled={uploading}
                            className="inline-flex items-center justify-center gap-2 bg-klein text-white px-4 py-3 text-sm rounded hover:bg-klein/90 disabled:opacity-50"
                            data-testid="recurring-bulk-upload"
                        >
                            <Upload className="w-4 h-4" />
                            {uploading ? "Uploading…" : "2. Upload filled file"}
                        </button>
                        <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={onUpload} data-testid="recurring-bulk-file" />
                    </div>

                    {result && (
                        <div className="border border-ink/10 rounded p-4 bg-bone/40 space-y-2 text-sm" data-testid="recurring-bulk-result">
                            <div className="flex justify-between"><span className="text-ink/60">Created</span><strong className="text-emerald-700">{result.created_count}</strong></div>
                            <div className="flex justify-between"><span className="text-ink/60">Skipped</span><strong>{result.skipped_count}</strong></div>
                            <div className="flex justify-between"><span className="text-ink/60">Not found</span><strong>{result.not_found_count}</strong></div>
                            <div className="flex justify-between border-t border-ink/10 pt-2"><span className="text-ink/60">Total monthly impact</span><strong className="text-klein">₦{Number(result.total_monthly).toLocaleString()}</strong></div>
                            {result.skipped_count > 0 && result.skipped[0] && (
                                <div className="text-[11px] text-ink/50 mt-2">e.g. Row {result.skipped[0].row}: {result.skipped[0].reason}</div>
                            )}
                        </div>
                    )}

                    <div className="text-right pt-2">
                        <button onClick={onClose} className="px-4 py-2 text-sm text-ink/60 hover:text-ink" data-testid="recurring-bulk-close-2">Close</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
